import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { toast } from 'react-hot-toast';
import { UPDATE_PORTFOLIO_GOALS } from '../graphql/mutations/portfolioGoals';
import { usePortfolioContext } from '../contexts/PortfolioContext';
import { GET_PORTFOLIO_SUMMARY } from '../graphql/queries/portfolioSummary';
import { formatCurrency, formatPercentage } from '../utils/formatters';

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const InvestmentGoalsModal = ({ isOpen, onClose, initialMultiple = 1.5, onContinue, initialGoal }) => {
  const { currentPortfolioId } = usePortfolioContext();
  const [saveGoals, { loading: saving }] = useMutation(UPDATE_PORTFOLIO_GOALS);
  const [multiple, setMultiple] = useState(clamp(initialMultiple, 1.5, 5));
  const [step, setStep] = useState('setup'); // 'setup' | 'configure'
  const [changeSource, setChangeSource] = useState('goal'); // 'goal' | 'dist'
  const [distribution, setDistribution] = useState({ large: 100, mid: 0, low: 0 });

  // Per-category target multiples (default; overridden by saved goal)
  const [targets, setTargets] = useState({ large: 2.0, mid: 3.0, low: 5.0 });

  const INVESTMENT_BASE = 10000;

  const fixedTargets = useMemo(
    () => targets || { large: 2.0, mid: 3.0, low: 5.0 },
    [targets]
  );

  const computeMultipleFromDistribution = useCallback(
    (dist) => {
      const weights = {
        large: (dist.large || 0) / 100,
        mid: (dist.mid || 0) / 100,
        low: (dist.low || 0) / 100,
      };
      const m = (fixedTargets.large * weights.large) + (fixedTargets.mid * weights.mid) + (fixedTargets.low * weights.low);
      return clamp(Number(m.toFixed(2)), 1.5, 5);
    },
    [fixedTargets]
  );

  useEffect(() => {
    if (!isOpen) return;
    const handleEscKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = previousOverflow || 'unset';
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    // Prefer using initialGoal passed from parent (already loaded on dashboard)
    if (initialGoal) {
      const nextDist = {
        large: Number(initialGoal.distLarge) || 0,
        mid: Number(initialGoal.distMid) || 0,
        low: Number(initialGoal.distLow) || 0,
      };
      setDistribution(nextDist);
      // Override with saved targets if present
      const tLarge = parseFloat(initialGoal.targetLarge);
      const tMid = parseFloat(initialGoal.targetMid);
      const tLow = parseFloat(initialGoal.targetLow);
      const nextTargets = {
        large: !isNaN(tLarge) && tLarge > 0 ? tLarge : 2.0,
        mid: !isNaN(tMid) && tMid > 0 ? tMid : 3.0,
        low: !isNaN(tLow) && tLow > 0 ? tLow : 5.0,
      };
      setTargets(nextTargets);

      const m = Number(initialGoal.targetMultiple);
      const derived = isNaN(m) ? computeMultipleFromDistribution(nextDist) : clamp(m, 1.5, 5);
      setMultiple(derived);
      setStep('configure');
      setChangeSource('goal');
      return;
    }
    // Otherwise use initialMultiple prop (only runs on initial open without saved goal)
    setChangeSource('goal');
    setMultiple(clamp(initialMultiple, 1.5, 5));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialGoal]);

  const riskColorClass = useMemo(() => {
    if (multiple < 3) return 'text-green-600 dark:text-green-400';
    if (multiple < 5) return 'text-yellow-600 dark:text-yellow-400';
    if (multiple < 7) return 'text-orange-600 dark:text-orange-400';
    return 'text-red-600 dark:text-red-400';
  }, [multiple]);

  const riskBgColorClass = useMemo(() => {
    if (multiple < 3) return 'bg-green-500';
    if (multiple < 5) return 'bg-yellow-500';
    if (multiple < 7) return 'bg-orange-500';
    return 'bg-red-600';
  }, [multiple]);

  const proceeds = useMemo(() => INVESTMENT_BASE * multiple, [multiple]);
  const roiPercent = useMemo(() => (multiple - 1) * 100, [multiple]);

  const computeSafeDistribution = (targetMultiple) => {
    // Smooth, preset-guided interpolation:
    // Anchors: A1(1.5)->100/0/0, A2(2.0)->80/15/5 (Safe), A3(3.0)->60/25/15 (Balanced), A4(5.0)->0/0/100
    const m = clamp(targetMultiple, 1.5, 5.0);

    const A1 = { m: 1.5, large: 100, mid: 0, low: 0 };
    const A2 = { m: 2.0, large: 80, mid: 15, low: 5 };
    const A3 = { m: 3.0, large: 60, mid: 25, low: 15 };
    const A4 = { m: 5.0, large: 0, mid: 0, low: 100 };

    const lerp = (a, b, t) => a + (b - a) * t;
    const blend = (P, Q, t) => ({
      large: lerp(P.large, Q.large, t),
      mid: lerp(P.mid, Q.mid, t),
      low: lerp(P.low, Q.low, t),
    });

    let dist;
    if (m <= A2.m) {
      const t = (m - A1.m) / (A2.m - A1.m);
      dist = blend(A1, A2, t);
    } else if (m <= A3.m) {
      const t = (m - A2.m) / (A3.m - A2.m);
      dist = blend(A2, A3, t);
    } else {
      const t = (m - A3.m) / (A4.m - A3.m);
      dist = blend(A3, A4, t);
    }

    // Snap to 5% steps and normalize to 100
    const snap5 = (x) => Math.round(x / 5) * 5;
    let L = clamp(snap5(dist.large), 0, 100);
    let M = clamp(snap5(dist.mid), 0, 100);
    let S = clamp(snap5(dist.low), 0, 100);
    let sum = L + M + S;
    if (sum !== 100) {
      // Adjust the largest share to make total 100
      const diff = 100 - sum;
      if (L >= M && L >= S) L = clamp(L + diff, 0, 100);
      else if (M >= L && M >= S) M = clamp(M + diff, 0, 100);
      else S = clamp(S + diff, 0, 100);
    }
    return { large: L, mid: M, low: S };
  };

  useEffect(() => {
    // Initialize or update distribution/targets only when goal slider is the source
    if (!isOpen) return;
    if (changeSource !== 'goal') return;
    const newDist = computeSafeDistribution(multiple);
    setDistribution(newDist);
  }, [multiple, changeSource, isOpen]);

  const round5 = (n) => Math.round(n / 5) * 5;

  const applyPreset = (preset) => {
    const next = { large: preset.large, mid: preset.mid, low: preset.low };
    setDistribution(next);
    setChangeSource('dist');
    setMultiple(computeMultipleFromDistribution(next));
  };

  const handleSave = async () => {
    if (!currentPortfolioId) { toast.error('No portfolio selected'); return; }
    try {
      const variables = {
        input: {
          portfolioID: currentPortfolioId,
          targetMultiple: multiple.toFixed(2),
          distLarge: distribution.large,
          distMid: distribution.mid,
          distLow: distribution.low,
          targetLarge: fixedTargets.large.toFixed(2),
          targetMid: fixedTargets.mid.toFixed(2),
          targetLow: fixedTargets.low.toFixed(2),
        }
      };
      const { data } = await saveGoals({ variables, refetchQueries: [{ query: GET_PORTFOLIO_SUMMARY, variables: { portfolioId: currentPortfolioId } }], awaitRefetchQueries: true });
      if (data?.updatePortfolioGoals?.success) {
        toast.success('Goals saved');
        try {
          const evt = new CustomEvent('goals:updated', { detail: { portfolioId: currentPortfolioId, targetMultiple: multiple } });
          window.dispatchEvent(evt);
        } catch (_) {}
        if (typeof onContinue === 'function') {
          try { onContinue(multiple, distribution, fixedTargets); } catch (_) {}
        }
        onClose();
      } else {
        toast.error(data?.updatePortfolioGoals?.error || 'Failed to save goals');
      }
    } catch (e) {
      toast.error(e.message || 'Failed to save goals');
    }
  };

  const setDistKeepingHundred = (key, value) => {
    const vNew = clamp(round5(value), 0, 100);
    const current = distribution[key];
    const delta = vNew - current;
    let next = { ...distribution };

    if (delta > 0) {
      // Increase selected: reduce more risky categories first
      const reduceOrder = ['low', 'mid', 'large'].filter((c) => c !== key);
      let need = delta;
      for (const cat of reduceOrder) {
        if (need <= 0) break;
        const take = Math.min(need, next[cat]);
        next[cat] = next[cat] - take;
        need -= take;
      }
      next[key] = current + delta;
    } else if (delta < 0) {
      // Decrease selected: add freed capacity to less risky categories first
      let free = -delta;
      const increaseOrder = ['large', 'mid', 'low'].filter((c) => c !== key);
      for (const cat of increaseOrder) {
        if (free <= 0) break;
        const capacity = 100 - next[cat];
        const add = Math.min(free, capacity);
        next[cat] = next[cat] + add;
        free -= add;
      }
      next[key] = vNew;
    } else {
      return; // no change
    }

    // Snap to 5% to avoid drift
    next = {
      large: clamp(round5(next.large), 0, 100),
      mid: clamp(round5(next.mid), 0, 100),
      low: clamp(round5(next.low), 0, 100),
    };

    // Normalize to ensure sum = 100 (adjust least risky when adding, most risky when subtracting)
    let sum = next.large + next.mid + next.low;
    if (sum !== 100) {
      const diff = 100 - sum; // positive means need to add
      if (diff > 0) {
        const order = ['large', 'mid', 'low'];
        for (const cat of order) {
          if (sum === 100) break;
          if (next[cat] <= 95) {
            const inc = Math.min(diff, 100 - next[cat], 5);
            next[cat] += inc;
            sum += inc;
          }
        }
      } else if (diff < 0) {
        const order = ['low', 'mid', 'large'];
        for (const cat of order) {
          if (sum === 100) break;
          if (next[cat] >= 5) {
            const dec = Math.min(-diff, next[cat], 5);
            next[cat] -= dec;
            sum -= dec;
          }
        }
      }
    }

    setDistribution(next);
    // Update goal multiple based on current targets (do not modify targets)
    const weights = { large: next.large / 100, mid: next.mid / 100, low: next.low / 100 };
    const newMultiple = (fixedTargets.large * weights.large) + (fixedTargets.mid * weights.mid) + (fixedTargets.low * weights.low);
    setChangeSource('dist');
    setMultiple(clamp(Number(newMultiple.toFixed(2)), 1.5, 5));
  };
  // Multiples are fixed; no target sliders/editing.

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-start justify-center overflow-y-auto">
      <div className="bg-white dark:bg-gray-800 rounded-md shadow-lg w-11/12 md:w-3/4 lg:w-1/2 mt-16 p-4">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Set Investment Goals</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">✕</button>
        </div>

        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Target return multiple</label>
            <div className={`text-base font-semibold ${riskColorClass}`}>{multiple.toFixed(1)}x</div>
          </div>

          <div className="relative py-2 select-none">
            <div className={`absolute left-0 right-0 top-1/2 -translate-y-1/2 h-2 rounded-full ${riskBgColorClass} opacity-70 pointer-events-none`}></div>
            <input
              type="range"
              min={1.5}
              max={5}
              step={0.1}
              value={multiple}
              onChange={(e) => { setChangeSource('goal'); setMultiple(clamp(parseFloat(e.target.value || '1.5'), 1.5, 5)); }}
              className="relative w-full appearance-none bg-transparent h-2 cursor-pointer"
            />
          </div>

          <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400 mt-1">
            <span>1.5x (safer)</span>
            <span>5x (riskier)</span>
          </div>
        </div>

        <div className="mb-3">
          <div className="flex items-center justify-center gap-4 text-sm text-gray-800 dark:text-gray-200">
            <span>
              Investment: <span className="font-semibold">{formatCurrency(INVESTMENT_BASE)}</span>
            </span>
            <span className="text-gray-400 dark:text-gray-500">•</span>
            <span>
              Proceeds: <span className="font-semibold">{formatCurrency(proceeds)}</span>
            </span>
            <span className="text-gray-400 dark:text-gray-500">•</span>
            <span>
              ROI: <span className={`font-semibold ${riskColorClass}`}>{formatPercentage(roiPercent)}</span>
            </span>
          </div>
        </div>

        {step === 'setup' ? (
          <>
            <div className="mb-4 p-3 rounded-md bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800">
              <p className="text-xs text-yellow-800 dark:text-yellow-200">
                Disclaimer: Gains are not guaranteed and outcomes depend heavily on your entry points and market conditions. Use this as guidance, not a promise of returns.
              </p>
            </div>

            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="px-3 py-1.5 rounded-md bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200">Cancel</button>
              <button onClick={() => setStep('configure')} className="px-3 py-1.5 rounded-md bg-blue-600 text-white">Continue</button>
            </div>
          </>
        ) : (
          <>
            {/* Allocation section */}
            <div className="mt-4">
              <div className="flex items-center justify-center gap-3 mb-8 flex-wrap">
                <button
                  type="button"
                  onClick={() => applyPreset({ large: 80, mid: 15, low: 5 })}
                  className="px-4 py-2 text-sm rounded-md bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/50"
                >
                  Safe (80/15/5)
                </button>
                <button
                  type="button"
                  onClick={() => applyPreset({ large: 60, mid: 25, low: 15 })}
                  className="px-4 py-2 text-sm rounded-md bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-900/50"
                >
                  Balanced (60/25/15)
                </button>
                <button
                  type="button"
                  onClick={() => applyPreset({ large: 40, mid: 30, low: 30 })}
                  className="px-4 py-2 text-sm rounded-md bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-900/50"
                >
                  Aggressive (40/30/30)
                </button>
              </div>
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
                    <span>Large Caps ($10B+)</span>
                    <span>{distribution.large}%</span>
                  </div>
                  <input type="range" min={0} max={100} step={5} value={distribution.large} onChange={(e) => setDistKeepingHundred('large', Number(e.target.value))} className="w-full" />
                </div>
                <div>
                  <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
                    <span>Mid Caps ($1B-10B)</span>
                    <span>{distribution.mid}%</span>
                  </div>
                  <input type="range" min={0} max={100} step={5} value={distribution.mid} onChange={(e) => setDistKeepingHundred('mid', Number(e.target.value))} className="w-full" />
                </div>
                <div>
                  <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
                    <span>Low Caps ($100M-1B)</span>
                    <span>{distribution.low}%</span>
                  </div>
                  <input type="range" min={0} max={100} step={5} value={distribution.low} onChange={(e) => setDistKeepingHundred('low', Number(e.target.value))} className="w-full" />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button onClick={onClose} className="px-3 py-1.5 rounded-md bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200">Close</button>
              <button onClick={handleSave} disabled={saving} className="px-3 py-1.5 rounded-md bg-blue-600 text-white disabled:opacity-60">
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default InvestmentGoalsModal;


